import { HalfaLaserBox } from "@/components/HalfaLaserStrip";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Minus, Plus, ArrowRight, Loader2, Pencil, Bookmark } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useMeta, inkHex } from "@/lib/meta";
import { api, errMsg, eur } from "@/lib/api";
import { StampSvg } from "@/components/StampSvg";
import { ProductThumb, sizeLabel } from "@/components/ProductThumb";

function Totals({ subtotal, meta }) {
  if (!meta) return <div className="text-sm text-slate-500">Calcolo totali…</div>;
  const free = subtotal >= (meta?.free_shipping_threshold || 0);
  const ship = free ? 0 : meta?.shipping_cost || 0;
  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between"><span>Subtotale</span><span data-testid="cart-subtotal">{eur(subtotal)}</span></div>
      <div className="flex justify-between"><span>Spedizione</span><span data-testid="cart-shipping">{ship ? eur(ship) : "Gratuita"}</span></div>
      {!free && <p className="text-[11px] text-slate-500">Spedizione gratuita da {eur(meta?.free_shipping_threshold)}</p>}
      <div className="flex justify-between border-t pt-3 text-lg font-bold"><span>Totale</span><span data-testid="cart-total">{eur(subtotal + ship)}</span></div>
      <p className="text-[11px] text-slate-500">Prezzi IVA inclusa</p>
    </div>
  );
}

const FIELDS = [
  ["name", "Nome e cognome *"], ["company", "Azienda"], ["vat", "P.IVA / Codice fiscale"], ["phone", "Telefono *"],
  ["address", "Indirizzo *"], ["city", "Città *"], ["zip", "CAP *"], ["province", "Provincia"],
];

export default function Cart() {
  const cart = useCart();
  const { user } = useAuth();
  const meta = useMeta();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [ship, setShip] = useState(null);
  const form = ship || {
    name: user?.name || "", company: user?.company || "", vat: user?.vat || "", phone: user?.phone || "",
    address: user?.address || "", city: user?.city || "", zip: user?.zip || "", province: user?.province || "", notes: "",
  };
  const set = (k, v) => setShip((p) => ({ ...(p || form), [k]: v }));
  useEffect(() => {
    if (user) setShip((p) => p && Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v || user[k] || ""])));
  }, [user]);
  const [accepted, setAccepted] = useState(false);
  const [toSave, setToSave] = useState({});
  const saveMine = async () => {
    const list = cart.items.filter((i) => i.layout && toSave[i.key] !== undefined);
    const res = await Promise.allSettled(list.map((i) => api.post("/my/stamps", { name: (toSave[i.key] || i.name).trim() || i.name, product_id: i.product_id, ink_color: i.ink_color, design: i.design, layout: { border: i.layout.border, elements: i.layout.elements } })));
    const ok = res.filter((r) => r.status === "fulfilled").length;
    if (ok) toast.success(`${ok} timbr${ok > 1 ? "i salvati" : "o salvato"} nei tuoi timbri`);
    if (ok < list.length) toast.error("Alcuni timbri non sono stati salvati");
  };

  const checkout = async (e, payLater = false) => {
    e.preventDefault();
    if (!accepted) return toast.error("Accetta Termini e condizioni per procedere");
    if (!user) return navigate("/accedi?next=/carrello");
    setBusy(true);
    try {
      await saveMine();
      setToSave({});
      const { data } = await api.post("/orders", {
        items: cart.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, ink_color: i.ink_color, pads: i.pads || 0, layout: i.layout ? { border: i.layout.border, elements: i.layout.elements } : null, design: i.design })),
        shipping: form,
        origin_url: window.location.origin,
        accept_terms: accepted,
        pay_later: payLater,
      });
      if (data.deferred) {
        cart.clear();
        toast.success(`Ordine ${data.number} inviato! Ti abbiamo mandato la conferma via email.`);
        return navigate("/account");
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  if (!cart.items.length)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center space-y-5" data-testid="cart-empty">
        <h1 className="text-5xl font-extrabold uppercase">Il carrello è vuoto</h1>
        <Link to="/catalogo" className="press inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-yellow text-brand-navy font-semibold">Scegli un timbro <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[1fr_420px] gap-10">
      <div className="space-y-4">
        <h1 className="text-5xl font-extrabold uppercase">Carrello</h1>
        {cart.items.map((it, idx) => (
          <div key={it.key} data-testid={`cart-item-${idx}`} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row gap-5 items-center">
            <div className="ink-grid rounded-md p-3 w-full sm:w-60 flex items-center justify-center shrink-0">
              {it.layout ? <StampSvg layout={it.layout} color={inkHex(meta, it.ink_color)} scale={Math.min(200 / it.layout.width_mm, 110 / it.layout.height_mm)} />
                : <ProductThumb p={{ kind: "pad", image: it.image, name: it.name }} w={160} h={100} />}
            </div>
            <div className="flex-1 w-full space-y-1">
              <Link to={`/timbro/${it.slug}`} className="font-display text-2xl font-bold uppercase hover:text-brand-navy">{it.name}</Link>
              <div className="text-xs text-slate-500 font-mono">{it.layout ? sizeLabel(it.layout) : `Cuscinetto${it.sku ? ` · Cod. ${it.sku}` : ""}`} · inchiostro {meta?.inks.find((x) => x.id === it.ink_color)?.name}</div>
              {it.pad_price > 0 && (
                <div className="flex items-center gap-2 pt-2 text-xs" data-testid={`cart-pad-row-${idx}`}>
                  <span className="w-3 h-3 rounded-full" style={{ background: inkHex(meta, it.ink_color) }} />
                  <span className="flex-1">{it.pad_name} {meta?.inks.find((x) => x.id === it.ink_color)?.name} · {eur(it.pad_price)}</span>
                  <div className="flex items-center border border-slate-200 rounded h-7">
                    <button data-testid={`cart-pad-dec-${idx}`} className="w-7 h-full flex items-center justify-center" onClick={() => cart.setPads(it.key, (it.pads || 0) - 1)}><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center font-mono" data-testid={`cart-pad-qty-${idx}`}>{it.pads || 0}</span>
                    <button data-testid={`cart-pad-inc-${idx}`} className="w-7 h-full flex items-center justify-center" onClick={() => cart.setPads(it.key, (it.pads || 0) + 1)}><Plus className="w-3 h-3" /></button>
                  </div>
                </div>
              )}
              {it.layout && (
                <div className="flex flex-wrap items-center gap-2 pt-2 text-xs" data-testid={`cart-save-row-${idx}`}>
                  <label className="flex items-center gap-1.5 font-semibold text-brand-navy cursor-pointer">
                    <input type="checkbox" data-testid={`cart-save-checkbox-${idx}`} className="accent-brand-navy w-4 h-4" checked={toSave[it.key] !== undefined}
                      onChange={(e) => setToSave((m) => { const n = { ...m }; if (e.target.checked) n[it.key] = it.name; else delete n[it.key]; return n; })} />
                    <Bookmark className="w-3.5 h-3.5" /> Salva nei miei timbri per riordinarlo velocemente
                  </label>
                  {toSave[it.key] !== undefined && (
                    <input data-testid={`cart-save-name-${idx}`} value={toSave[it.key]} maxLength={120} onChange={(e) => setToSave((m) => ({ ...m, [it.key]: e.target.value }))}
                      placeholder="Nome del timbro" className="h-8 flex-1 min-w-[160px] px-2 rounded border border-slate-300 text-xs" />
                  )}
                </div>
              )}
              <div className="flex items-center justify-between pt-3">
                <div className="flex items-center border border-slate-300 rounded-md h-9">
                  <button data-testid={`cart-qty-dec-${idx}`} className="w-9 h-full flex items-center justify-center" onClick={() => cart.setQty(it.key, it.quantity - 1)}><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${idx}`}>{it.quantity}</span>
                  <button data-testid={`cart-qty-inc-${idx}`} className="w-9 h-full flex items-center justify-center" onClick={() => cart.setQty(it.key, it.quantity + 1)}><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex items-center gap-4">
                  {it.layout && <Link to={`/timbro/${it.slug}?modifica=${it.key}`} data-testid={`cart-edit-${idx}`} className="flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-brand-navy"><Pencil className="w-3.5 h-3.5" /> Modifica</Link>}
                  <span className="text-right">
                    <span className="block font-bold" data-testid={`cart-line-total-${idx}`}>{eur(cart.lineTotal(it))}</span>
                    <span className="block text-[10px] text-slate-500">{eur(it.unit_price)} cad.{it.base_price && it.unit_price < it.base_price - 0.001 ? ` · -${Math.round((1 - it.unit_price / it.base_price) * 100)}%` : ""}</span>
                  </span>
                  <button data-testid={`cart-remove-${idx}`} onClick={() => cart.remove(it.key)} className="text-slate-400 hover:text-brand-navy"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
        <HalfaLaserBox where="cart" />
      </div>
      <form onSubmit={checkout} className="rounded-xl border border-slate-200 bg-white p-6 space-y-5 self-start lg:sticky lg:top-[110px]" data-testid="checkout-form">
        <h2 className="text-2xl font-extrabold uppercase">Spedizione e pagamento</h2>
        {user ? (
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, l]) => (
              <label key={k} className={`text-xs space-y-1 ${["name", "address"].includes(k) ? "col-span-2" : ""}`}>
                <span className="text-slate-600">{l}</span>
                <input data-testid={`checkout-${k}-input`} value={form[k]} onChange={(e) => set(k, e.target.value)} required={l.includes("*")}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70" />
              </label>
            ))}
            <label className="col-span-2 text-xs space-y-1">
              <span className="text-slate-600">Note per la produzione</span>
              <textarea data-testid="checkout-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
                className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70" />
            </label>
          </div>
        ) : (
          <p className="text-sm text-slate-600">Accedi o crea un account per completare l'ordine. Se eri già cliente di halfatimbri.it usa le stesse credenziali.</p>
        )}
        <Totals subtotal={cart.subtotal} meta={meta} />
        <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="checkout-terms-label">
          <input type="checkbox" data-testid="checkout-terms-checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 accent-brand-navy w-4 h-4" required />
          <span>Ho letto e accetto i <Link to="/termini" target="_blank" className="underline font-semibold">Termini e condizioni</Link> e la <Link to="/privacy" target="_blank" className="underline font-semibold">Privacy Policy</Link>. Prendo atto che i timbri personalizzati sono esclusi dal diritto di recesso (art. 59, lett. c, Codice del Consumo); per gli accessori posso recedere entro 14 giorni con il pulsante "Recedere dal contratto qui" in fondo a ogni pagina e nel mio account (<Link to="/recesso" target="_blank" className="underline">dettagli</Link>).</span>
        </label>
        {user?.pay_later ? (
          <>
            <button type="button" data-testid="checkout-send-order-btn" disabled={busy} onClick={(e) => { const f = e.currentTarget.form; if (f.reportValidity()) checkout(e, true); }}
              className="press w-full h-12 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center justify-center gap-2 hover:bg-brand-yellowdark disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Invia ordine
            </button>
            <button data-testid="checkout-submit-btn" disabled={busy} className="w-full text-xs text-slate-500 underline">oppure paga ora con carta</button>
          </>
        ) : (
          <button data-testid="checkout-submit-btn" disabled={busy} className="press w-full h-12 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center justify-center gap-2 hover:bg-brand-yellowdark disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {user ? "Paga in modo sicuro" : "Accedi per ordinare"}
          </button>
        )}
      </form>
    </div>
  );
}
